import React, { useState, useMemo } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Calculator, CheckCircle2, Send, AlertTriangle, ExternalLink } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { formatPrice } from "../lib/format";
import { inquiriesApi } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { localeFor } from "../lib/format";

export default function Financing() {
  const { t, lang } = useI18n();
  const { toast } = useToast();
  const [price, setPrice] = useState(30000);
  const [down, setDown] = useState(5000);
  const [months, setMonths] = useState(60);
  const [rate, setRate] = useState(4.9);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const monthly = useMemo(() => {
    const principal = Math.max(price - down, 0);
    const r = rate / 100 / 12;
    if (r === 0) return principal / months;
    return (principal * r) / (1 - Math.pow(1 + r, -months));
  }, [price, down, months, rate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await inquiriesApi.create({
        type: "financing",
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: t("financing.subject"),
        message: `${t("financing.price")}: ${formatPrice(price, localeFor(lang))} | ${t("financing.down")}: ${formatPrice(down, localeFor(lang))} | ${t("financing.duration")}: ${months} ${t("financing.durationUnit")} | ${t("financing.rate")}: ${rate}% | ${t("financing.estimate")}: ${formatPrice(monthly, localeFor(lang))}`,
        extra: { price, down, months, rate, monthly: Math.round(monthly * 100) / 100 },
      });
      toast({ title: t("financing.toastTitle"), description: t("financing.toastDesc") });
      setForm({ name: "", email: "", phone: "" });
    } catch {
      toast({ title: t("car.sendFailed"), description: t("car.sendFailedDesc") });
    }
  };

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />
      <Toaster />

      <section className="pt-[140px] pb-12 px-5 lg:px-8">
        <div className="max-w-[1100px] mx-auto text-center">
          <div className="text-[#c5a76f] text-sm tracking-[0.3em] uppercase mb-4">{t("financing.overline")}</div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("financing.title")} <span className="text-[#c5a76f]">{t("financing.titleAccent")}</span>
          </h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("financing.intro")}
          </p>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-6">
        <div className="max-w-[1200px] mx-auto">
          <div
            data-testid="financing-legal-warning-banner"
            className="flex items-start gap-3 p-4 bg-[#c5a76f]/10 border-l-4 border-[#c5a76f] rounded-r-md"
          >
            <AlertTriangle className="w-5 h-5 text-[#c5a76f] flex-shrink-0 mt-0.5" />
            <p className="text-[#c5a76f] text-sm font-semibold uppercase tracking-wide">
              {t("financing.legalWarning")}
            </p>
          </div>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-24">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-[#242424] border border-white/5 rounded-md p-7 lg:p-10">
            <div className="flex items-center gap-3 mb-7">
              <Calculator className="w-6 h-6 text-[#c5a76f]" />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
                {t("financing.calcTitle")}
              </h2>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2 text-sm">
                  <label className="text-white/70">{t("financing.price")}</label>
                  <span className="text-[#c5a76f] font-semibold">{formatPrice(price)}</span>
                </div>
                <input type="range" min="5000" max="200000" step="500" value={price} onChange={(e) => setPrice(Number(e.target.value))} className="w-full accent-[#c5a76f]" />
              </div>
              <div>
                <div className="flex justify-between mb-2 text-sm">
                  <label className="text-white/70">{t("financing.down")}</label>
                  <span className="text-[#c5a76f] font-semibold">{formatPrice(down)}</span>
                </div>
                <input type="range" min="0" max={price} step="500" value={down} onChange={(e) => setDown(Number(e.target.value))} className="w-full accent-[#c5a76f]" />
              </div>
              <div>
                <div className="flex justify-between mb-2 text-sm">
                  <label className="text-white/70">{t("financing.duration")}</label>
                  <span className="text-[#c5a76f] font-semibold">{months} {t("financing.durationUnit")}</span>
                </div>
                <input type="range" min="12" max="84" step="6" value={months} onChange={(e) => setMonths(Number(e.target.value))} className="w-full accent-[#c5a76f]" />
              </div>
              <div>
                <div className="flex justify-between mb-2 text-sm">
                  <label className="text-white/70">{t("financing.rate")}</label>
                  <span className="text-[#c5a76f] font-semibold">{rate.toFixed(2)}%</span>
                </div>
                <input type="range" min="2" max="10" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full accent-[#c5a76f]" />
              </div>
            </div>

            <div className="mt-10 p-6 bg-gradient-to-br from-[#c5a76f]/15 to-transparent border border-[#c5a76f]/30 rounded-md">
              <div className="text-white/60 text-xs uppercase tracking-widest mb-2">{t("financing.estimate")}</div>
              <div className="text-5xl font-bold text-[#c5a76f]" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
                {formatPrice(monthly)}
              </div>
              <div className="text-white/50 text-xs mt-3">
                {t("financing.disclaimer")}
              </div>
            </div>

            <a
              href="https://klantenportaal.financo.be/customer?utm_source=trprem"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="financing-financo-cta"
              className="mt-5 group flex items-center justify-between gap-4 p-5 bg-[#c5a76f] hover:bg-[#d8bd86] text-black rounded-md transition-colors"
            >
              <div className="text-left">
                <div className="text-sm font-bold tracking-wider uppercase flex items-center gap-2">
                  {t("financing.applyDirect")}
                  <ExternalLink className="w-4 h-4" />
                </div>
                <div className="text-xs mt-1 opacity-80 leading-snug">
                  {t("financing.applyDirectHint")}
                </div>
              </div>
            </a>
          </div>

          <div className="space-y-6">
            <div className="bg-[#242424] border border-white/5 rounded-md p-7 lg:p-10">
              <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
                {t("financing.proposalTitle")}
              </h2>
              <form onSubmit={onSubmit} className="space-y-4">
                <input required placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
                <input required type="email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
                <input placeholder={t("common.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
                <button className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase rounded">
                  {t("financing.askProposal")} <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
            <div className="bg-[#242424] border border-white/5 rounded-md p-7 lg:p-10">
              <h3 className="text-xl font-bold text-white mb-5">{t("financing.optionsTitle")}</h3>
              <ul className="space-y-3">
                {[
                  t("financing.opt1"),
                  t("financing.opt2"),
                  t("financing.opt3"),
                  t("financing.opt4"),
                  t("financing.opt5"),
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-white/80 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#c5a76f] mt-0.5" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-24">
        <div
          data-testid="financing-legal-block"
          className="max-w-[1200px] mx-auto bg-[#141414] border border-white/10 rounded-md p-7 lg:p-8"
        >
          <div className="text-[#c5a76f] text-xs uppercase tracking-[0.25em] mb-3">
            {t("financing.legalTitle")}
          </div>
          <p className="text-white/70 text-sm leading-relaxed mb-3">
            <span className="font-semibold text-white">{t("financing.legalWarning")}</span>{" "}
            {t("financing.legalBody")}
          </p>
          <p className="text-white/55 text-xs leading-relaxed mb-2">
            {t("financing.legalBroker")}
          </p>
          <p className="text-white/40 text-xs leading-relaxed">
            {t("financing.legalCompany")}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
